import type * as THREE from 'three';
import type { ColliderHandle, RoomCapabilities, RoomEnvironment, RoomModule, RoomModuleDefinition, RoomTick } from '@/game/roomModule';
import type { RoomDiagnostics } from '@/game/roomDiagnostics';
import { getAsset } from '@/models/registry';
import { kestnerAsset } from '@/models/env/kestner';
import { dogAsset, findDogParts, type DogParts } from '@/models/dog';
import { hollyAsset } from '@/models/holly';
import { HollyRig } from '@/anim/hollyRig';
import { applyDogPose } from '@/anim/dogPose';
import { HollyPresenter, drainHollyBellQueue } from '@/game/hollyPresenter';
import { CompanionBell, type CompanionBellEvent } from '@/systems/companionBell';
import { HOLLY_BELL_STRIDE_METRES, HOLLY_BELL_SOUND, bellEmissionRadius } from '@/audio/sounds/bell';
import type { HallSolid } from '@/world/solids';
import { DawnHerdView } from '@/world/dawnHerd';
import { collapseRigged, instanceGroup } from '@/world/instancing';
import { buildFenceBoards } from '@/models/env/fence-boards';
import {
  createLoadingYard, loadingYardSolids, RACE_GATE_OPEN_YAW, raceGateLeafSolid, TIMBER_PATCHED_BAY, VEHICLE_GATE, VEHICLE_GATE_OPEN_ANGLE,
  vehicleGateLeafSolid, vehicleGateLeafTip, YARD, yardDawnLighting, yardDawnZone,
} from '@/world/loadingYard';
import { IT_FAILS_CUES, IT_FAILS_SECONDS } from '@/story/act3/itFails';
import {
  GATE_JAM_ANGLE, IT_FAILS_SPAWN, ItFailsStaging, OFFSTAGE_BELL_AT, OFFSTAGE_BELLS, type ItFailsStage,
} from '@/world/yardFailsStaging';
import type { CarriedPose } from '@/physics/character';

/**
 * Beat 13's yard: the dawn yard, seconds after beat 12, where it goes wrong
 * (`docs/beat-13-plan.md`).
 *
 * **A room id of its own (`yard-fails`), not a mode of `yard-dawn`.** Beat 12's
 * room is a crew with cones, a junction box, a capture path and Holly's call;
 * beat 13 must have none of them (D-201: no stealth system is constructed at
 * all), and a flag inside beat 12's module would have made every one of them
 * a conditional that a later edit could get wrong. The geometry is shared —
 * `createLoadingYard({ dawn: true, vehicleGate: 'live' })` — so this is the
 * same place at the same hour, not a copy (S-9).
 *
 * **This module has no `stealth` field, and that is the S-11 gate's subject.**
 * `beat-13-fails.spec.ts` asserts it; the browser spec asserts the host built
 * no system from it.
 *
 * Everything that moves is decided by `ItFailsStaging` on the fixed step;
 * this file draws it, voices it, and turns the jam into colliders.
 */

/** Presentation time is clamped per frame, as the office's is: a hidden tab must not skip the swing. */
const MAX_FRAME = 0.1;
/** The two dawn fixtures beat 12's mark 1 cut. The handover contract opens with them out. */
const CUT_FIXTURES = ['race-flood', 'shed-door-lamp'] as const;

/**
 * The gateway, held. From the first tick the herd's head fills the throat,
 * and from the jam the half-shut leaf and the man in the gap do: either way a
 * 32 cm pig does not walk out through it. Without this, a leaf jammed at 40°
 * leaves 2 m of the gateway open, and the beat's "the gate jams" would be a
 * gate she could walk round — then the surge would carry her back in through
 * a fence she had already left by. Invisible: what she sees stop her is the
 * bodies, the leaf and his boots. `beat-13-fails-boot.spec.ts` drives her at
 * it (plan gate 5).
 */
export const GATEWAY_HELD: HallSolid = {
  name: 'yard-fails-gateway-held',
  half: { x: (VEHICLE_GATE.maxX - VEHICLE_GATE.minX) / 2, y: 0.6, z: 0.25 },
  pos: { x: VEHICLE_GATE.centreX, y: 0.6, z: YARD.maxZ - 0.3 },
  colour: 0,
  kind: 'metal',
  visible: false,
};

export const YARD_FAILS_LEAF_OPEN_ID = 'yard-fails-leaf-left-open';
export const YARD_FAILS_LEAF_JAMMED_ID = 'yard-fails-leaf-left-jammed';

const STAGE_FOR_CUE: Readonly<Record<string, ItFailsStage>> = {
  [IT_FAILS_CUES.jam]: 'the-jam',
  [IT_FAILS_CUES.whistle]: 'the-whistle',
  [IT_FAILS_CUES.bell]: 'the-bell',
  [IT_FAILS_CUES.surge]: 'the-surge',
  [IT_FAILS_CUES.outside]: 'outside',
};

function requirePivot(root: THREE.Object3D, name: string): THREE.Object3D {
  const node = root.getObjectByName(name);
  if (node === undefined) throw new Error(`yard-fails: perimeter gate lost its "${name}" pivot`);
  return node;
}

export function createYardFailsRoomModule(
  options: { part: number; dressing: boolean },
  capabilities: RoomCapabilities,
): RoomModule {
  const room = createLoadingYard({ dawn: true, paddock: options.dressing, vehicleGate: 'live' });
  capabilities.scene.add(room.group);
  const colliders: ColliderHandle[] = loadingYardSolids({ dawn: true, vehicleGate: 'live' }).map((solid) => capabilities.colliders.add(solid));
  colliders.push(capabilities.colliders.add(GATEWAY_HELD));
  // Holly is holding the race gate when the beat opens (the handover), so
  // its leaf is open and solid where beat 12 left it.
  room.raceGateLeaf.rotation.y = RACE_GATE_OPEN_YAW;
  colliders.push(capabilities.colliders.add(raceGateLeafSolid(RACE_GATE_OPEN_YAW, 'yard-fails-race-leaf-open')));

  // ── The vehicle gate, live: the first leaf in the yard that moves ─────────
  const gate = getAsset('perimeter-gate')?.create();
  if (gate === undefined) throw new Error('yard-fails: perimeter-gate asset is not registered');
  gate.name = 'yard-fails-vehicle-gate';
  gate.position.set(VEHICLE_GATE.centreX, 0, VEHICLE_GATE.z);
  const leftLeaf = requirePivot(gate, 'leaf-left');
  const rightLeaf = requirePivot(gate, 'leaf-right');
  leftLeaf.rotation.y = VEHICLE_GATE_OPEN_ANGLE.left;
  rightLeaf.rotation.y = -VEHICLE_GATE_OPEN_ANGLE.right;
  // Raw, the asset is every bar and rail a mesh in every pass: the first
  // census put the room at 523 draw calls against the dawn yard's 420.
  // Merged about its two leaves, which are all that moves.
  collapseRigged(gate, [leftLeaf, rightLeaf]);
  capabilities.scene.add(gate);

  // ── The bay she goes through ────────────────────────────────────────────
  // Whole until the surge, then sprung: the herd's press takes the patch out
  // the way beat 7's roll did, falling OUTWARD (north) — the broken state's
  // debris lies along its local +X, so the yard turns it by −π/2 where the
  // hedgerow turns it by +π/2. Presentation only: nothing walks here after
  // the surge except the carry, and the fence's collider stays. That the
  // carry holds her on every tick from the surge to the close is what makes
  // the sprung-open-but-solid bay unreachable; `beat-13-fails.spec.ts` gates
  // it (playthrough audit row 18).
  const bayWhole = room.group.getObjectByName('yard-timber-patch');
  if (bayWhole === undefined) throw new Error('yard-fails: the timber-patched bay is missing from the yard');
  const bayBroken = instanceGroup(buildFenceBoards('broken'), [{ x: TIMBER_PATCHED_BAY.x, z: TIMBER_PATCHED_BAY.z, rotY: -TIMBER_PATCHED_BAY.rotY }], 'yard-fails-timber-sprung');
  bayBroken.visible = false;
  capabilities.scene.add(bayBroken);
  colliders.push(capabilities.colliders.add(vehicleGateLeafSolid('right', VEHICLE_GATE_OPEN_ANGLE.right)));
  // The left leaf is solid where it stands and where it stops, and nowhere
  // between: in motion it stops nothing (the wool shed's door, and beat 12's
  // race gate). Swapped on the FIXED step the staging reports the jam on.
  let leafOpen: ColliderHandle | null = capabilities.colliders.add(vehicleGateLeafSolid('left', VEHICLE_GATE_OPEN_ANGLE.left, undefined, YARD_FAILS_LEAF_OPEN_ID));
  let leafJammed: ColliderHandle | null = null;

  // ── The movers ─────────────────────────────────────────────────────────────
  const staging = new ItFailsStaging({ surge: IT_FAILS_SECONDS.surge });
  // The standing asset, hat on: the role, back on (the plan). Not the office's
  // seated variant, and nothing of his is a watcher.
  const kestner = kestnerAsset.create();
  // Whole: nothing of his turns in this beat (the asset keeps his neck live
  // for the yard's look-round), and the census has no draw call to spare.
  collapseRigged(kestner, []);
  capabilities.scene.add(kestner);
  const hollyRoot = hollyAsset.create();
  capabilities.scene.add(hollyRoot);
  const hollyPresenter = new HollyPresenter(hollyRoot, new HollyRig(hollyRoot));
  const bell = new CompanionBell({ strideDistance: HOLLY_BELL_STRIDE_METRES, soundRadius: bellEmissionRadius() }, staging.snapshot().holly);
  const bellQueue: CompanionBellEvent[] = [];
  let hollyWas = staging.snapshot().holly;
  const dogs: { root: THREE.Group; parts: DogParts }[] = [0, 1].map(() => {
    const root = dogAsset.create();
    const parts = findDogParts(root);
    // Merged again about only what `applyDogPose` turns here — legs, neck and
    // tail. The asset keeps its head, ears and breath live for the beats that
    // look and pant; these two run and are never closer than the rig. 21
    // meshes a dog put the room over the dawn yard's ceiling (census, below).
    collapseRigged(root, [parts.neck, parts.tail, ...parts.legs]);
    capabilities.scene.add(root);
    return { root, parts };
  });
  // Beat 12's instanced herd, posed from the staging rather than queued.
  const herdView = new DawnHerdView();
  capabilities.scene.add(herdView.group);

  /** Copied out of the borrowed tick; the surge's carry starts where she was. */
  const her = { x: IT_FAILS_SPAWN.x, z: IT_FAILS_SPAWN.z };
  let bells = 0;
  let offstageBells = 0;
  let lightsSet = false;
  let renderClock = 0;
  let dogStride = 0;
  let disposed = false;

  const voice = (id: string, at: { x: number; y: number; z: number }, gain: number, detune?: number): void => {
    capabilities.playOneShot(id, detune === undefined ? { gain, position: at } : { gain, position: at, detune });
  };

  return {
    // No interactables and no `callVerb`: `F` does nothing and offers nothing
    // for the whole beat (the plan: a call that did nothing would be the
    // cruellest input in the game, and one that did something a branch).
    interactables: [],
    fixedStep(tick: RoomTick): void {
      her.x = tick.player.x;
      her.z = tick.player.z;
      staging.step(tick.dt);
      const state = staging.snapshot();
      if (state.jammed && leafOpen !== null) {
        capabilities.colliders.remove(leafOpen);
        leafOpen = null;
        leafJammed = capabilities.colliders.add(vehicleGateLeafSolid('left', GATE_JAM_ANGLE, undefined, YARD_FAILS_LEAF_JAMMED_ID));
        const tip = vehicleGateLeafTip('left', GATE_JAM_ANGLE);
        // The stop is metal on the herd's press: beat 6's race clang, and the
        // herd's stall under it.
        voice('race-clang', { x: tip.x, y: 0.8, z: tip.z }, 0.8, -200);
        voice('herd-stall', { x: tip.x, y: 0.5, z: tip.z - 1.5 }, 0.7);
      }
      if (state.hollyVisible) {
        for (const event of bell.advance(hollyWas, state.holly)) {
          bellQueue.push(event);
          bells += 1;
        }
      }
      hollyWas = state.holly;
      if (state.stage === 'outside') {
        for (const [index, at] of OFFSTAGE_BELLS.entries()) {
          if (offstageBells === index && state.stageTime >= at) {
            voice(HOLLY_BELL_SOUND, OFFSTAGE_BELL_AT, 0.42, index * 12);
            offstageBells += 1;
          }
        }
      }
      if (state.dogsVisible && state.dogs.some((dog) => dog.moving)) {
        dogStride += tick.dt;
        if (dogStride >= 0.26) {
          dogStride = 0;
          const lead = state.dogs[0];
          if (lead !== undefined) voice('dog-claws-concrete', { x: lead.x, y: 0, z: lead.z }, 0.5);
        }
      }
    },
    get carry(): CarriedPose | null {
      return staging.snapshot().carry;
    },
    handleCue(cue): void {
      const stage = STAGE_FOR_CUE[cue.id];
      if (stage === undefined) return;
      staging.enter(stage, her);
      const state = staging.snapshot();
      if (stage === 'the-whistle') {
        // Beat 6's handler whistle, from him: the whole of what he says.
        voice('handler-whistle', { x: state.kestner.x, y: 1.6, z: state.kestner.z }, 0.75);
      }
      if (stage === 'the-surge') {
        bayWhole.visible = false;
        bayBroken.visible = true;
        voice('boards-break', { x: TIMBER_PATCHED_BAY.x, y: 0.4, z: TIMBER_PATCHED_BAY.z }, 0.6);
      }
      if (stage === 'the-surge' || stage === 'the-whistle') {
        voice('herd-shuffle', { x: her.x, y: 0.4, z: her.z }, 0.7);
      }
    },
    renderStep(frameDt: number): void {
      if (disposed) return;
      const dt = Math.min(Math.max(0, frameDt), MAX_FRAME);
      renderClock += dt;
      if (!lightsSet) {
        for (const name of CUT_FIXTURES) capabilities.lights.scale(name, 0);
        lightsSet = true;
      }
      const state = staging.snapshot();
      leftLeaf.rotation.y = state.gateAngle;
      kestner.position.set(state.kestner.x, 0, state.kestner.z);
      kestner.rotation.y = state.kestner.yaw;
      herdView.updatePoses(state.herd, renderClock);
      hollyRoot.visible = state.hollyVisible;
      if (state.hollyVisible) {
        hollyPresenter.update(frameDt, state.holly, { latchProgress: 1, doorProgress: 0, riseProgress: 0 }, null);
      }
      if (bellQueue.length > 0) drainHollyBellQueue(bellQueue, capabilities.playOneShot);
      state.dogs.forEach((pose, index) => {
        const dog = dogs[index];
        if (dog === undefined) return;
        dog.root.visible = state.dogsVisible;
        dog.root.position.set(pose.x, 0, pose.z);
        dog.root.rotation.y = pose.yaw;
        applyDogPose(dog.parts, { walking: pose.moving, noseDown: false, casting: false, tail: pose.moving, clock: renderClock + index * 0.4 });
      });
    },
    framing: () => null,
    diagnostics: (): RoomDiagnostics => {
      const state = staging.snapshot();
      return {
        kind: 'yard-fails',
        yardFails: {
          stage: state.stage,
          gateAngle: state.gateAngle,
          jammed: state.jammed,
          leafSolid: leafJammed !== null ? 'jammed' : 'open',
          bells,
          offstageBells,
          carry: state.carry,
          herdNorthOfFence: state.herd.filter((pose) => pose.z > YARD.maxZ).length,
          hollyVisible: state.hollyVisible,
          kestner: { x: state.kestner.x, z: state.kestner.z },
        },
      };
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const collider of colliders) capabilities.colliders.remove(collider);
      if (leafOpen !== null) capabilities.colliders.remove(leafOpen);
      if (leafJammed !== null) capabilities.colliders.remove(leafJammed);
      capabilities.scene.remove(room.group);
      capabilities.scene.remove(gate);
      capabilities.scene.remove(bayBroken);
      capabilities.scene.remove(kestner);
      capabilities.scene.remove(hollyRoot);
      for (const dog of dogs) capabilities.scene.remove(dog.root);
      capabilities.scene.remove(herdView.group);
      herdView.dispose();
    },
  };
}

/** The dawn yard's light and sky, unchanged: the same morning, a minute later. */
function yardFailsEnvironment(): RoomEnvironment {
  const zone = yardDawnZone();
  return {
    zone,
    lighting: yardDawnLighting(),
    background: { kind: 'sky', seed: 12, zenith: zone.fill, middle: zone.fog },
    spawn: IT_FAILS_SPAWN,
    // The ramp's pump is what beat 12's mark 1 stopped; nothing hums here.
    ambience: [],
  };
}

export const YARD_FAILS_ROOM_MODULE: RoomModuleDefinition = {
  room: 'yard-fails',
  // The same yard at the same hour: her map is beat 6's sheet, as beat 12's is.
  ledgerSheet: 'yard',
  environment: yardFailsEnvironment,
  create: createYardFailsRoomModule,
};
